import React, { useState, useEffect } from 'react';
import { XMarkIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import api, { chatAPI } from '../../../services/api';

const StudentNewChatModal = ({ isOpen, onClose, onConversationCreated }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [staff, setStaff] = useState([]);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [activeTab, setActiveTab] = useState('students');

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        if (activeTab === 'students') {
          const response = await api.get('/api/students/?skip=0&limit=1000&active_only=true');
          setStudents(response.data || []);
        } else {
          const response = await api.get('/api/employees/?skip=0&active_only=true&limit=1000');
          setStaff(response.data.employees || []);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    if (isOpen) {
      fetchData();
    }
  }, [isOpen, activeTab]);

  const handleCreateConversation = async (participant) => {
    try {
      setCreating(true);
      const participantId = participant._id || participant.id;
      const participantType = activeTab === 'students' ? 'student' : 'employee';

      const response = await chatAPI.createStudentConversation(participantId, participantType);
      onConversationCreated(response.data);
      onClose();
      setSearchTerm('');
    } catch (error) {
      console.error('Error creating conversation:', error);
      alert('Failed to start conversation. Please try again.');
    } finally {
      setCreating(false);
    }
  };

  const filteredData = activeTab === 'students'
    ? students.filter(student => {
        const fullName = `${student.first_name} ${student.last_name}`.toLowerCase();
        return fullName.includes(searchTerm.toLowerCase()) ||
               student.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
               student.student_id?.toLowerCase().includes(searchTerm.toLowerCase());
      })
    : staff.filter(member => {
        const fullName = `${member.first_name} ${member.last_name}`.toLowerCase();
        return fullName.includes(searchTerm.toLowerCase()) ||
               member.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
               member.designation?.toLowerCase().includes(searchTerm.toLowerCase()) ||
               member.employee_id?.toLowerCase().includes(searchTerm.toLowerCase());
      });

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-end sm:items-center justify-center min-h-screen text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div
          className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"
          onClick={onClose}
        />

        {/* Modal panel */}
        <div className="inline-block w-full h-full sm:h-auto sm:max-w-2xl sm:my-8 overflow-hidden text-left align-bottom sm:align-middle transition-all transform bg-gray-900 sm:rounded-lg shadow-xl border-t sm:border border-gray-700">
          {/* Header */}
          <div className="flex items-center justify-between px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-700">
            <h3 className="text-base sm:text-lg font-medium text-gray-100">Start New Conversation</h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-300 transition-colors p-2 -mr-2 min-h-[44px] min-w-[44px] flex items-center justify-center"
              aria-label="Close modal"
            >
              <XMarkIcon className="w-6 h-6" />
            </button>
          </div>

          {/* Tabs */}
          <div className="flex border-b border-gray-700">
            <button
              onClick={() => setActiveTab('students')}
              className={`flex-1 px-4 sm:px-6 py-3 text-sm font-medium transition-colors min-h-[48px] ${
                activeTab === 'students'
                  ? 'text-blue-400 border-b-2 border-blue-500 bg-gray-800'
                  : 'text-gray-400 hover:text-gray-300 hover:bg-gray-800'
              }`}
            >
              Students
            </button>
            <button
              onClick={() => setActiveTab('staff')}
              className={`flex-1 px-4 sm:px-6 py-3 text-sm font-medium transition-colors min-h-[48px] ${
                activeTab === 'staff'
                  ? 'text-blue-400 border-b-2 border-blue-500 bg-gray-800'
                  : 'text-gray-400 hover:text-gray-300 hover:bg-gray-800'
              }`}
            >
              Staff
            </button>
          </div>

          {/* Search */}
          <div className="px-4 sm:px-6 py-3 sm:py-4">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                <MagnifyingGlassIcon className="w-5 h-5 text-gray-500" />
              </div>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder={activeTab === 'students' ? 'Search students...' : 'Search staff members...'}
                className="w-full pl-10 pr-4 py-2.5 text-base border border-gray-700 bg-gray-800 text-gray-100 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent placeholder-gray-500"
              />
            </div>
          </div>

          {/* List */}
          <div className="px-4 sm:px-6 pb-4 sm:pb-6">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
              </div>
            ) : filteredData.length === 0 ? (
              <div className="text-center py-12 text-gray-400">
                <p>No {activeTab === 'students' ? 'students' : 'staff members'} found</p>
                {searchTerm && (
                  <p className="text-sm mt-1">Try a different search term</p>
                )}
              </div>
            ) : (
              <div className="max-h-[60vh] sm:max-h-96 overflow-y-auto space-y-2">
                {filteredData.map((person) => (
                  <button
                    key={person._id || person.id}
                    onClick={() => handleCreateConversation(person)}
                    disabled={creating}
                    className="w-full flex items-center p-3 rounded-lg border border-gray-700 hover:border-blue-500 hover:bg-gray-800 active:bg-gray-750 transition-all disabled:opacity-50 disabled:cursor-not-allowed min-h-[64px]"
                  >
                    {/* Avatar */}
                    <div className="flex-shrink-0">
                      <div className={`h-10 w-10 rounded-full ${activeTab === 'students' ? 'bg-green-500' : 'bg-blue-500'} flex items-center justify-center text-white font-semibold`}>
                        {person.first_name?.charAt(0).toUpperCase()}
                      </div>
                    </div>

                    {/* Info */}
                    <div className="ml-3 flex-1 text-left">
                      <p className="text-sm font-medium text-gray-100">
                        {`${person.first_name} ${person.last_name}`}
                      </p>
                      <p className="text-xs text-gray-400">
                        {person.email}
                      </p>
                      {activeTab === 'students' ? (
                        person.student_id && (
                          <p className="text-xs text-gray-500">
                            ID: {person.student_id}
                          </p>
                        )
                      ) : (
                        person.designation && (
                          <p className="text-xs text-gray-500">
                            {person.designation}
                          </p>
                        )
                      )}
                    </div>

                    {/* Badge */}
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                      activeTab === 'students'
                        ? 'bg-green-900 text-green-300'
                        : 'bg-blue-900 text-blue-300'
                    }`}>
                      {activeTab === 'students' ? 'Student' : 'Staff'}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default StudentNewChatModal;
